import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCartItems, saveCartItems } from "../../utils/cartUtils";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import defaultProductImage from "../../images/Default-Image.jpeg";
import "./CartDetails.css";

const CartDetails = () => {
  const navigate = useNavigate();
  const showPrices = useCanViewPrices();
  const [cartItems, setCartItems] = useState([]);

  // Load cart items from localStorage on mount
  useEffect(() => {
    const items = getCartItems();
    if (items && items.length > 0) {
      const normalizedCart = items.map((item) => ({
        ...item,
        quantity: item.quantity || 1,
        price: item.price || 0,
        name: item.name || item.title || "Unknown Product",
        image: item.image || "",
      }));
      setCartItems(normalizedCart);
    } else {
      setCartItems([]);
    }

    // Listen for cart updates
    const handleCartUpdate = () => {
      const updatedItems = getCartItems();
      if (updatedItems && updatedItems.length > 0) {
        const normalizedCart = updatedItems.map((item) => ({
          ...item,
          quantity: item.quantity || 1,
          price: item.price || 0,
          name: item.name || item.title || "Unknown Product",
          image: item.image || "",
        }));
        setCartItems(normalizedCart);
      } else {
        setCartItems([]);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);


  // Update quantity
  const updateQuantity = (itemId, change) => {
    setCartItems((prevItems) => {
      const updated = prevItems.map((item) => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + change);
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      saveCartItems(updated);
      return updated;
    });
  };

  // Remove item from cart
  const removeItem = (itemId) => {
    setCartItems((prevItems) => {
      const updated = prevItems.filter((item) => item.id !== itemId);
      saveCartItems(updated);
      return updated;
    });
  };

  // Calculate totals
  const shippingCost = 5.0;
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <div className="cart-details-container">
      {/* Main Content Area - Full Width */}
      <div className="cart-main-content-full">
        {/* Cart Title and Image Section */}
        <div className="cart-header-section">
          <div className="cart-title-section">
            <h1 className="cart-title">Cart</h1>
          </div>
          <div className="cart-image-showcase">
            <div className="product-showcase-image">
              {/* Image is set as CSS background */}
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="cart-tabs">
          <button className="cart-tab active">All</button>
          <button className="cart-tab">Parts</button>
          <button className="cart-tab">Accessories</button>
          <button className="cart-tab">Devices</button>
        </div>

        {/* Main Content Row - Cart and Order Summary Side by Side */}
        <div className="cart-main-row">
          {/* Cart Items Section - Left Side */}
          <div className="cart-items-section">
            {cartItems.length === 0 ? (
              <div className="empty-cart-section">
                <div className="empty-cart-icon-large">
                  <div className="cart-icon-outline"></div>
                </div>
                <div className="empty-cart-message">
                  <h2>Your cart is empty!</h2>
                  <div className="empty-cart-actions">
                    <span>Create a Quick Order!</span>
                    <span className="or-text">or</span>
                    <span>Continue Shopping!</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="cart-items-card">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-image">
                      <img
                        src={item.image || defaultProductImage}
                        alt={item.name}
                        className={
                          (item.image || defaultProductImage) === defaultProductImage
                            ? "cart-item-img cart-item-img--default"
                            : "cart-item-img"
                        }
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultProductImage;
                          e.target.classList.add("cart-item-img--default");
                        }}
                      />
                    </div>
                    <div className="cart-item-info">
                      <div className="cart-item-name">{item.name}</div>
                      <button
                        className="cart-item-remove-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="cart-item-quantity">
                      <span className="quantity-label">Qty</span>
                      <div className="quantity-controls">
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.id, -1)}
                        >
                          -
                        </button>
                        <input
                          type="text"
                          className="quantity-input"
                          value={item.quantity}
                          readOnly
                        />
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.id, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {showPrices && (
                      <div className="cart-item-price">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Summary - Right Side */}
          <div className="cart-order-summary-sidebar">
            <div className="order-summary-inline">
              {/* Top Bar */}
              <div className="order-summary-bar"></div>

              <div className="order-summary-title">
                Items ({cartItems.length})
              </div>

              <div className="order-summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="order-summary-item">
                    <div className="order-item-name">{item.name}</div>
                    {showPrices && (
                      <div className="order-item-price">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="order-summary-shipping">
                <div className="order-shipping-label">Shipping</div>
                {showPrices && (
                  <div className="order-shipping-price">${shippingCost.toFixed(2)}</div>
                )}
              </div>

              <div className="order-summary-divider"></div>

              <div className="order-summary-subtotal">
                <div className="order-subtotal-label">Subtotal</div>
                {showPrices && (
                  <div className="order-subtotal-amount">
                    ${subtotal.toFixed(2)}
                  </div>
                )}
              </div>

              <button 
                className="checkout-btn"
                onClick={() => navigate("/checkout")}
              >
                Proceed to Checkout
              </button>
              <div className="buy-now-text">Buy Now with</div>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="cart-action-buttons">
          <button className="action-btn">CSV</button>
          <button className="action-btn">Forward Cart</button>
          <button
            className="action-btn"
            onClick={() => {
              setCartItems([]);
              saveCartItems([]);
            }}
            disabled={cartItems.length === 0}
          >
            Clear Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartDetails;
